import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import {
  CardBodyComponent,
  CardComponent,
  StateMessageComponent,
} from '@groundworks/ngx-groundworks-ui';
import { AppointmentHistoryItem } from '../../models/appointment-history-item.model';
import {
  DeactivateOpportunityRequest,
  QuoteInfo,
  SaleApprovalChecklistItems,
  SaleInfo,
} from '../../sdk/sdk';
import { ModalService } from '../../services/modal.service';
import { PendingSalesStateService } from '../../services/pending-sales-state.service';
import { AppointmentHistoryComponent } from '../appointment-history/appointment-history.component';
import { SalesApprovalChecklistComponent } from '../sales-approval-checklist/sales-approval-checklist.component';

/**
 * Consolidated component for displaying pending sale activity information.
 * Supports two variants:
 * - 'actions': Shows appointment history and sales approval checklist (2-column layout)
 * - 'overview': Shows appointment history only with "Remove from Follow-Up" button (1-column layout)
 */
@Component({
  selector: 'app-pending-sale-activity',
  imports: [
    CommonModule,
    CardComponent,
    CardBodyComponent,
    AppointmentHistoryComponent,
    SalesApprovalChecklistComponent,
    StateMessageComponent,
  ],
  templateUrl: './pending-sale-activity.component.html',
})
export class PendingSaleActivityComponent {
  private readonly stateService = inject(PendingSalesStateService);
  private readonly modalService = inject(ModalService);

  variant = input<'actions' | 'overview'>('actions');
  isPendingSale = input<boolean>(true);

  appointments = input<AppointmentHistoryItem[]>([]);
  quote = input<QuoteInfo | null>(null);
  opportunityId = input<string | null>(null);
  canSaleBeApproved = input<boolean>(false);
  readonly isLoadingInput = input<boolean>(false);
  readonly hasErrorInput = input<boolean>(false);

  readonly showChecklist = computed(() => this.variant() === 'actions');
  readonly showRemoveButton = computed(() => this.variant() === 'overview');
  readonly layoutColumns = computed(() => (this.variant() === 'actions' ? [8, 4] : [12]));
  readonly title = computed(() =>
    this.variant() === 'actions' ? 'Pending Sales Actions' : 'Pending Sales Overview',
  );

  readonly state = computed(() =>
    this.stateService.computeState(
      this.isLoadingInput(),
      this.hasErrorInput(),
      this.appointments(),
      this.quote(),
    ),
  );

  getState(): string {
    return this.state();
  }

  quoteCompletedItemsChange = output<SaleApprovalChecklistItems>();
  saleCreated = output<{ quoteId: string; sale: SaleInfo }>();
  appointmentCreated = output<AppointmentHistoryItem>();
  removeFromFollowUp = output<DeactivateOpportunityRequest>();
  dispositionUpdated = output<void>();

  getCompletedItems(): SaleApprovalChecklistItems {
    return this.stateService.getCompletedItems(this.quote());
  }

  getSystemVerifiedItems(): SaleApprovalChecklistItems {
    return this.stateService.getSystemVerifiedItems(this.quote());
  }

  hasSale(): boolean {
    return this.stateService.hasSale(this.quote());
  }

  onCompletedItemsChange(items: SaleApprovalChecklistItems): void {
    this.quoteCompletedItemsChange.emit(items);
  }

  onSaleCreated(event: { quoteId: string; sale: SaleInfo }): void {
    this.saleCreated.emit(event);
  }

  onAppointmentCreated(appointment: AppointmentHistoryItem): void {
    this.appointmentCreated.emit(appointment);
  }

  onDispositionUpdated(): void {
    this.dispositionUpdated.emit();
  }

  onScheduleRevisitClick(): void {
    this.stateService.openScheduleRevisit(this.opportunityId());
  }

  onRemoveFromFollowUpClick(): void {
    const modalRef = this.modalService.openRemoveFromFollowUpModal(this.opportunityId() ?? '');

    modalRef.result.then(
      (result: { opportunityId: string; reason: string }) => {
        const request = new DeactivateOpportunityRequest({
          removeReason: result.reason,
        });

        this.removeFromFollowUp.emit(request);
      },
      () => {
        // User canceled - noop
      },
    );
  }
}
